'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { trackChatEvent } from '@/app/lib/guideChat/analytics';
import { CHAT_LABELS } from '@/app/lib/guideChat/labels';
import { applyCollectedToLoanStore } from '@/app/lib/guideChat/prefill';
import { claimOpenerSlot, clearSession, loadSession, markOpenerDismissed, saveSession } from '@/app/lib/guideChat/session';
import { guideChatKindForPath, openerKey } from '@/app/lib/guideChat/triggers';
import { isGuideChatProduct, type Citation, type CollectedQuoteData, type FormStep, type GuideChatProduct, type HistoryTurn, type OpenerResponse, type OpenerTrigger, type PageContext, type PageKind, type QuestionSource } from '@/app/lib/guideChat/types';
import { askGuide, fetchConversationState, fetchOpener } from './api';
import GuideChatPanel, { ChatTurn } from './GuideChatPanel';
import ProactiveOpener from './ProactiveOpener';
import { FUNNEL_STAGE_BY_KIND, buildPageContext, hasQuotationWizard } from './pageContext';
import { useContentPageTriggers } from './useContentPageTriggers';
import { useFormTriggers } from './useFormTriggers';
import { useQuoteFormOnScreen } from './useQuoteFormOnScreen';

interface ActiveOpener {
  opener: OpenerResponse;
  trigger: OpenerTrigger;
  context: PageContext;
  shownAt: number;
  placement: 'bottom' | 'top';
}

let turnSeq = 0;
const nextTurnId = () => `t${Date.now()}_${turnSeq++}`;

function turnsFromHistory(history: HistoryTurn[]): ChatTurn[] {
  return history.map((h) => ({ id: nextTurnId(), question: h.question, response: { answer: h.answer, ...(h.citations ? { citations: h.citations } : {}) } }));
}

function slimCitations(citations: unknown): Citation[] | undefined {
  if (!Array.isArray(citations)) return undefined;
  const slim = citations
    .filter((c): c is Citation => !!c && typeof c === 'object' && typeof (c as Citation).url === 'string')
    .map((c) => ({ url: c.url, ...(typeof c.title === 'string' ? { title: c.title } : {}) }));
  return slim.length ? slim : undefined;
}

function historyFromTurns(turns: ChatTurn[]): HistoryTurn[] {
  return turns
    .filter((t) => t.question && t.response?.answer)
    .map((t) => ({ question: t.question as string, answer: t.response?.answer as string, ...(slimCitations(t.response?.citations) ? { citations: slimCitations(t.response?.citations) } : {}) }));
}

// Turns already on screen come back in the backend history: only those sent while it loaded are kept.
export function mergeResumedTurns(prev: ChatTurn[], history: HistoryTurn[], shownBefore: ReadonlySet<string>): ChatTurn[] {
  return [
    { id: nextTurnId(), question: null, notice: CHAT_LABELS.resumeNotice },
    ...turnsFromHistory(history),
    ...prev.filter((t) => t.question && !shownBefore.has(t.id)),
  ];
}

interface Props {
  kind: PageKind;
  product?: GuideChatProduct;
}

// The conversation keeps the product of its last answer, unless the visitor moved to another product's pages: the page then wins.
export function initialProduct(session: { product?: GuideChatProduct | null; pageProduct?: GuideChatProduct | null }, pageProduct: GuideChatProduct): GuideChatProduct {
  if (session.pageProduct && session.pageProduct !== pageProduct) return pageProduct;
  return session.product ?? pageProduct;
}

export default function GuideChatWidget({ kind, product: pageProduct = 'loan' }: Props) {
  const pathname = usePathname() ?? '';
  const router = useRouter();
  const launcherRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>(() => turnsFromHistory(loadSession().history));
  const [conversationId, setConversationId] = useState<string | null>(() => loadSession().conversationId);
  const [product, setProduct] = useState<GuideChatProduct>(() => initialProduct(loadSession(), pageProduct));
  const [collected, setCollected] = useState<CollectedQuoteData>(() => loadSession().collected ?? {});
  const [closed, setClosed] = useState<boolean>(() => loadSession().closed === true);
  const [activeOpener, setActiveOpener] = useState<ActiveOpener | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const quoteFormOnScreen = useQuoteFormOnScreen();
  // A conversation started in this page view is already on screen: only one carried over from another page is resumed.
  const resumedRef = useRef(turns.length === 0);
  const openRef = useRef(false);
  const openerRef = useRef<ActiveOpener | null>(null);
  const turnsRef = useRef(turns);
  useEffect(() => {
    openRef.current = open;
    openerRef.current = activeOpener;
    turnsRef.current = turns;
  }, [open, activeOpener, turns]);

  useEffect(() => {
    saveSession({ pageProduct, product: initialProduct(loadSession(), pageProduct) });
  }, [pageProduct]);

  const showOpener = useCallback(
    async (trigger: OpenerTrigger, step?: FormStep | null) => {
      if (openRef.current || openerRef.current) return;
      const context = buildPageContext(kind, pageProduct);
      if (step !== undefined) context.step = step === 'borrower' && context.step === 'health' ? 'health' : step;
      if (loadSession().closed) return;
      if (!claimOpenerSlot(openerKey(pathname, context.step))) return;
      const session = loadSession();
      const { opener, fromBackend, conversationId: mintedId } = await fetchOpener({ ...(session.conversationId ? { conversationId: session.conversationId } : {}), pageContext: context, trigger });
      if (mintedId && !loadSession().conversationId) {
        saveSession({ conversationId: mintedId });
        setConversationId(mintedId);
      }
      if (openRef.current) return;
      // A field under the keyboard must stay visible: on small screens the bubble goes to the top.
      const fieldActive = kind === 'form' && !!context.field && window.innerWidth < 768;
      setActiveOpener({ opener, trigger, context, shownAt: Date.now(), placement: fieldActive ? 'top' : 'bottom' });
      // The backend already journals the shown event when it composed the opener.
      if (!fromBackend) trackChatEvent('chat.opener.shown', { conversationId: session.conversationId, openerId: opener.openerId, trigger, pageContext: context });
    },
    [kind, pageProduct, pathname],
  );

  const contentTriggersEnabled = kind !== 'form' && !open && !activeOpener && !quoteFormOnScreen;
  useContentPageTriggers({ enabled: contentTriggersEnabled, kind: kind === 'form' ? 'article' : kind, onTrigger: (t) => void showOpener(t) });

  const [wizardMounted, setWizardMounted] = useState(false);
  useEffect(() => {
    if (kind !== 'form') return;
    const check = () => setWizardMounted(hasQuotationWizard());
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [kind]);
  useFormTriggers({ enabled: kind === 'form' && wizardMounted && !open, onTrigger: (t, step) => void showOpener(t, step) });

  const dismissOpener = useCallback(() => {
    const current = openerRef.current;
    if (!current) return;
    const session = loadSession();
    markOpenerDismissed(openerKey(pathname, current.context.step));
    trackChatEvent('chat.opener.dismissed', { conversationId: session.conversationId, openerId: current.opener.openerId, trigger: current.trigger, dismissedAfterMs: Date.now() - current.shownAt });
    setActiveOpener(null);
  }, [pathname]);

  const resumeIfNeeded = useCallback(async (id: string) => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    const shownBefore = new Set(turnsRef.current.map((t) => t.id));
    const state = await fetchConversationState(id);
    if (!state) return;
    setConversationId(state.conversationId);
    setCollected(state.collected);
    if (state.product) setProduct(state.product);
    // The backend journal has no citations: take them back from the local copy of the same turns.
    const local = loadSession().history;
    const history = state.history.map((h, i) => {
      const mine = local[i];
      return mine && mine.question === h.question && mine.answer === h.answer && mine.citations ? { ...h, citations: mine.citations } : h;
    });
    setTurns((prev) => mergeResumedTurns(prev, history, shownBefore));
    saveSession({ conversationId: state.conversationId, history, collected: state.collected });
    trackChatEvent('chat.session.resumed', { conversationId: state.conversationId, turns: state.history.length });
  }, []);

  const openPanel = useCallback(
    (source: 'launcher' | 'opener') => {
      setOpen(true);
      const session = loadSession();
      trackChatEvent('chat.widget.opened', { conversationId: session.conversationId, source, pageContext: buildPageContext(kind, pageProduct) });
      if (session.conversationId && session.history.length > 0) void resumeIfNeeded(session.conversationId);
    },
    [kind, pageProduct, resumeIfNeeded],
  );

  const send = useCallback(
    async (question: string, questionSource: QuestionSource) => {
      if (busy || closed) return;
      const id = nextTurnId();
      const history = turns.filter((t) => t.question && t.response?.answer).map((t) => ({ question: t.question as string, answer: t.response?.answer as string }));
      setTurns((prev) => [...prev, { id, question, loading: true }]);
      setSuggestions([]);
      setBusy(true);
      const result = await askGuide({
        question,
        history,
        ...(conversationId ? { conversationId } : {}),
        funnelStage: FUNNEL_STAGE_BY_KIND[kind],
        pageContext: buildPageContext(kind, pageProduct),
        collected,
        product,
        questionSource,
      });
      if (!result.ok) {
        setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, loading: false, unavailable: result.unavailable ?? undefined, error: result.error } : t)));
        setBusy(false);
        return;
      }
      const data = result.data;
      const nextConversationId = typeof data.conversationId === 'string' ? data.conversationId : conversationId;
      // PHP serialises an empty collection as [] rather than {}.
      const nextCollected = data.collected && typeof data.collected === 'object' && !Array.isArray(data.collected) ? data.collected : collected;
      const nextProduct = isGuideChatProduct(data.product) ? data.product : product;
      if (nextConversationId) setConversationId(nextConversationId);
      setCollected(nextCollected);
      setProduct(nextProduct);
      setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, loading: false, response: data } : t)));
      const nowClosed = data.conversationClosed === true;
      if (nowClosed) setClosed(true);
      saveSession({
        conversationId: nextConversationId,
        history: [...historyFromTurns(turns), { question, answer: data.answer ?? '', ...(slimCitations(data.citations) ? { citations: slimCitations(data.citations) } : {}) }],
        collected: nextCollected,
        product: nextProduct,
        pageProduct,
        ...(nowClosed ? { closed: true } : {}),
      });
      setBusy(false);
    },
    [busy, closed, turns, conversationId, collected, kind, product, pageProduct],
  );

  const acceptOpener = useCallback(
    (suggestion?: string) => {
      const current = openerRef.current;
      if (!current) return;
      trackChatEvent('chat.opener.accepted', { conversationId: loadSession().conversationId, openerId: current.opener.openerId, trigger: current.trigger, suggestion: suggestion ?? null });
      setActiveOpener(null);
      setTurns((prev) => [...prev, { id: nextTurnId(), question: null, response: { answer: current.opener.message } }]);
      setSuggestions(current.opener.suggestions);
      openPanel('opener');
      if (suggestion) void send(suggestion, 'suggestion');
    },
    [openPanel, send],
  );

  const feedback = useCallback(
    (turnId: string, rating: 'up' | 'down') => {
      const turnIndex = turns.filter((t) => t.question).findIndex((t) => t.id === turnId);
      setTurns((prev) => prev.map((t) => (t.id === turnId ? { ...t, feedback: rating } : t)));
      trackChatEvent('chat.feedback', { conversationId, turnIndex, rating });
    },
    [turns, conversationId],
  );

  const switchToForm = useCallback(
    (prefillUrl: string) => {
      const result = applyCollectedToLoanStore(collected);
      trackChatEvent('chat.form.switched', { conversationId, prefillUrl, prefilledFields: result.applied, estimatedFields: result.estimated });
      // Already inside the comparator: the store is filled in place, navigating would reset the wizard.
      if (guideChatKindForPath(pathname) === 'form') {
        setOpen(false);
        return;
      }
      router.push(prefillUrl);
    },
    [collected, conversationId, pathname, router],
  );

  const reset = useCallback(() => {
    clearSession();
    resumedRef.current = true;
    setTurns([]);
    setConversationId(null);
    setCollected({});
    setSuggestions([]);
    setClosed(false);
    setProduct(pageProduct);
  }, [pageProduct]);

  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      {activeOpener && !open && !quoteFormOnScreen && (
        <ProactiveOpener opener={activeOpener.opener} placement={activeOpener.placement} onDismiss={dismissOpener} onAccept={acceptOpener} />
      )}
      <button
        ref={launcherRef}
        type="button"
        onClick={() => (open ? close() : openPanel('launcher'))}
        aria-expanded={open}
        aria-controls={open ? 'guide-chat-title' : undefined}
        aria-label={open ? CHAT_LABELS.launcherClose : CHAT_LABELS.launcherOpen}
        className={`fixed bottom-6 right-4 z-[65] ${quoteFormOnScreen && !open ? 'hidden' : 'flex'} h-12 items-center gap-2 rounded-full bg-rm-blue-deep px-4 text-sm font-semibold text-white shadow-lg hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-rm-blue-deep md:right-6`}
      >
        <i aria-hidden="true" className="fa-regular fa-comment-question text-lg" />
        <span className="hidden md:inline">{CHAT_LABELS.launcher}</span>
      </button>
      <GuideChatPanel
        open={open}
        turns={turns}
        busy={busy}
        closed={closed}
        conversationId={conversationId}
        product={product}
        suggestions={suggestions}
        launcherRef={launcherRef}
        onClose={close}
        onSend={(q, source) => void send(q, source)}
        onFeedback={feedback}
        onSwitchToForm={switchToForm}
        onReset={reset}
      />
    </>
  );
}
